import { useCallback, useEffect, useState } from 'react';

import { notifications } from '@mantine/notifications';

import { vnextApiClient } from '@/services/vnext-api';
import {
  ConfigExportPayload,
  ImportOnConflict,
  ReportConfig,
  ReportConfigComponentBase,
  ReportConfigCreateRequest,
  ReportConfigSummary,
  ReportConfigUpdateRequest,
} from '@/types';

interface UseReportConfigsParams {
  teamId?: string | number;
  isActive?: boolean;
  autoFetch?: boolean;
  /**
   * Grid mode: fetch ONE page (server-side search via `q`) instead of the
   * whole catalogue. Pickers leave these unset and get everything — the
   * same endpoint serves both (vNext-api docs/list-endpoints.md §3).
   */
  page?: number;
  pageSize?: number;
  q?: string;
}

export function useReportConfigs(params: UseReportConfigsParams = {}) {
  const { teamId, isActive, autoFetch = true, page, pageSize, q } = params;

  const [configs, setConfigs] = useState<ReportConfigSummary[]>([]);
  /** Rows across all pages (grid mode); the catalogue size otherwise. */
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchConfigs = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      if (page !== undefined) {
        const result = await vnextApiClient.getReportConfigsPage({
          team_id: teamId,
          is_active: isActive,
          q: q || undefined,
          page,
          page_size: pageSize,
        });
        setConfigs(result.items);
        setTotal(result.total);
        return result.items;
      }
      const data = await vnextApiClient.getReportConfigs({
        team_id: teamId,
        is_active: isActive,
      });
      setConfigs(data);
      setTotal(data.length);
      return data;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch report configs');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [teamId, isActive, page, pageSize, q]);

  const getConfig = useCallback(
    async (configId: string): Promise<ReportConfig> => {
      try {
        setIsLoading(true);
        setError(null);
        return await vnextApiClient.getReportConfig(configId, teamId);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to fetch report config');
        setError(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [teamId]
  );

  const createConfig = useCallback(
    async (data: ReportConfigCreateRequest): Promise<ReportConfig> => {
      try {
        setIsLoading(true);
        setError(null);
        const created = await vnextApiClient.createReportConfig(data);

        notifications.show({
          title: 'Success',
          message: `Configuration "${created.name}" has been created`,
          color: 'green',
        });

        await fetchConfigs();
        return created;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to create report config');
        setError(error);

        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });

        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchConfigs]
  );

  const updateConfig = useCallback(
    async (configId: string, data: ReportConfigUpdateRequest): Promise<ReportConfig> => {
      try {
        setIsLoading(true);
        setError(null);
        const updated = await vnextApiClient.updateReportConfig(configId, data, teamId);

        notifications.show({
          title: 'Success',
          message: 'Configuration updated successfully',
          color: 'green',
        });

        await fetchConfigs();
        return updated;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to update report config');
        setError(error);

        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });

        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [teamId, fetchConfigs]
  );

  const deleteConfig = useCallback(
    async (configId: string): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);
        await vnextApiClient.deleteReportConfig(configId, teamId);

        notifications.show({
          title: 'Success',
          message: 'Configuration deleted successfully',
          color: 'green',
        });

        await fetchConfigs();
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to delete report config');
        setError(error);

        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });

        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [teamId, fetchConfigs]
  );

  const duplicateConfig = useCallback(
    async (configId: string, newName: string, newSlug: string): Promise<ReportConfig> => {
      try {
        setIsLoading(true);
        setError(null);
        const duplicated = await vnextApiClient.duplicateReportConfig(
          configId,
          newName,
          newSlug,
          teamId
        );

        notifications.show({
          title: 'Success',
          message: `Configuration duplicated as "${duplicated.name}"`,
          color: 'green',
        });

        await fetchConfigs();
        return duplicated;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to duplicate report config');
        setError(error);

        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });

        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [teamId, fetchConfigs]
  );

  const exportConfig = useCallback(
    async (
      configId: string
    ): Promise<{ payload: ConfigExportPayload; filename: string | null }> => {
      try {
        setIsLoading(true);
        setError(null);
        return await vnextApiClient.exportReportConfig(configId, teamId);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to export report config');
        setError(error);

        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });

        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [teamId]
  );

  /**
   * Import a config. Unlike the other mutators, errors do **not** auto-
   * toast — the modal owns the surface for 400 (missing scope
   * components) and 409 (slug conflict), which need structured rendering
   * rather than a generic toast. Success still toasts and refetches.
   */
  const importConfig = useCallback(
    async (
      payload: ConfigExportPayload,
      onConflict: ImportOnConflict = 'error'
    ): Promise<ReportConfig> => {
      setIsLoading(true);
      setError(null);
      try {
        const created = await vnextApiClient.importReportConfig(payload, {
          teamId,
          onConflict,
        });

        notifications.show({
          title: 'Success',
          message: `Configuration "${created.name}" has been imported`,
          color: 'green',
        });

        await fetchConfigs();
        return created;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to import report config');
        setError(error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [teamId, fetchConfigs]
  );

  const setComponents = useCallback(
    async (configId: string, components: ReportConfigComponentBase[]): Promise<ReportConfig> => {
      try {
        setIsLoading(true);
        setError(null);
        const updated = await vnextApiClient.setConfigComponents(configId, components, teamId);

        notifications.show({
          title: 'Success',
          message: 'Components updated successfully',
          color: 'green',
        });

        return updated;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to update components');
        setError(error);

        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });

        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [teamId]
  );

  const updateComponent = useCallback(
    async (
      configId: string,
      scopeComponentId: string,
      data: ReportConfigComponentBase
    ): Promise<ReportConfig> => {
      try {
        setIsLoading(true);
        setError(null);
        const updated = await vnextApiClient.updateConfigComponent(
          configId,
          scopeComponentId,
          data,
          teamId
        );

        notifications.show({
          title: 'Success',
          message: 'Component updated successfully',
          color: 'green',
        });

        return updated;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to update component');
        setError(error);

        notifications.show({
          title: 'Error',
          message: error.message,
          color: 'red',
        });

        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [teamId]
  );

  useEffect(() => {
    if (autoFetch) {
      fetchConfigs();
    }
  }, [autoFetch, fetchConfigs]);

  return {
    configs,
    total,
    isLoading,
    error,
    fetchConfigs,
    getConfig,
    createConfig,
    updateConfig,
    deleteConfig,
    duplicateConfig,
    exportConfig,
    importConfig,
    setComponents,
    updateComponent,
  };
}
